// src/pages/Issues/IssueList.js
import React, { useState } from 'react';
import { Box, Container, Typography, Button, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FilterListIcon from '@mui/icons-material/FilterList';

import PageLayout from '../../components/layout/PageLayout';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import Can from '../../components/permissions/Can';

// Import components
import IssueTable from '../../components/Issues/IssueTable';
import IssueFilters from '../../components/Issues/IssueFilters';
import IssueActionsMenu from '../../components/Issues/IssueActionsMenu';
import StatusDialog from '../../components/Issues/StatusDialog';
import AssignDialog from '../../components/Issues/AssignDialog';
import AssignedUsersDialog from '../../components/Issues/AssignedUsersDialog';

// Import hooks
import { useIssues } from '../../hooks/useIssues';
import { useIssueActions } from '../../hooks/useIssueActions';

const IssueList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // Add permission-based conditions
  const canViewListIssue = hasPermission('issue-list');
  const canViewIssue = hasPermission('issue-view');
  const canEditIssue = hasPermission('issue-edit');
  const canChangeIssueStatus = hasPermission('issue-status');
  const canAssignIssue = hasPermission('issue-assign-user');
  const canDeleteIssue = hasPermission('issue-delete');
  const canViewAssignedUsers = hasPermission('issue-assigned-users-list');

  // Use custom hooks
  const issuesData = useIssues(canViewListIssue);
  const issueActions = useIssueActions(issuesData, {
    canChangeIssueStatus,
    canAssignIssue,
    canDeleteIssue,
    canViewListIssue
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = (e, issue) => {
    if (!canViewListIssue) return;
    setAnchorEl(e.currentTarget);
    setSelectedIssue(issue);
    issueActions.setSelectedIssue(issue);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedIssue(null);
    issueActions.setSelectedIssue(null);
  };

  // If user doesn't have view permission, show access denied
  if (!canViewListIssue) {
    return (
      <PageLayout>
        <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            Access Denied - You don't have permission to view issues
          </Typography>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Issues List ({issuesData.issues.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <IssueTable.SearchBar
                searchValue={issuesData.searchValue}
                onSearchChange={issuesData.handleSearchChange}
                onClearSearch={issuesData.handleClearSearch}
                placeholder="Search issues..."
              />
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  position: 'relative',
                  ...(issuesData.getActiveFiltersCount() > 0 && {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.light',
                  })
                }}
              >
                Filters
                {issuesData.getActiveFiltersCount() > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {issuesData.getActiveFiltersCount()}
                  </Box>
                )}
              </Button>
            </Box>
          </Box>

          {/* Filters Section */}
          <IssueFilters
            showFilters={showFilters}
            filters={issuesData.filters}
            sort={issuesData.sort}
            usersList={issuesData.usersList}
            statusOptions={issuesData.statusOptions}
            statusFilterLoading={issuesData.statusFilterLoading}
            usersFilterLoading={issuesData.usersFilterLoading}
            onStatusOpen={issuesData.ensureStatusFilterOptions}
            onUsersOpen={issuesData.ensureUsersFilterOptions}
            onFilterChange={issuesData.handleFilterChange}
            onSortChange={issuesData.handleSortChange}
            onClearFilters={issuesData.clearFilters}
          />

          {/* Table */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              <IssueTable
                issues={issuesData.issues}
                loading={issuesData.tableLoading}
                pagination={issuesData.pagination}
                sort={issuesData.sort}
                onOpenMenu={handleOpenMenu}
                processingId={issuesData.processingId}
                onPageChange={issuesData.handlePageChange}
                onRowsPerPageChange={issuesData.handleRowsPerPageChange}
                onSortChange={issuesData.handleSortChange}
                canViewListIssue={canViewListIssue}
              />
            </CardContent>
          </Card>
      </Container>

      {/* Action Menu */}
      <IssueActionsMenu
        anchorEl={anchorEl}
        selectedIssue={selectedIssue}
        onClose={handleCloseMenu}
        onViewIssue={issueActions.handleViewIssue}
        onEditIssue={issueActions.handleEditIssue}
        onOpenStatusDialog={async () => {
          await issuesData.ensureStatusFilterOptions();
          issueActions.handleOpenStatusDialog(selectedIssue);
        }}
        onOpenAssignDialog={async () => {
          await issuesData.ensureUsersFilterOptions();
          issueActions.handleOpenAssignDialog(selectedIssue);
        }}
        onOpenAssignedUsersDialog={() => issueActions.handleOpenAssignedUsersDialog(selectedIssue)}
        onDeleteIssue={issueActions.handleDeleteIssue}
        permissions={{
          canViewIssue,
          canEditIssue,
          canChangeIssueStatus,
          canAssignIssue,
          canDeleteIssue,
          canViewAssignedUsers
        }}
      />

      {/* Dialogs */}
      {canChangeIssueStatus && (
        <StatusDialog
          open={issueActions.dialogs.status}
          selectedIssue={selectedIssue}
          newStatus={issueActions.statusData.newStatus}
          description={issueActions.statusData.description}
          statusOptions={issuesData.statusOptions}
          processingId={issuesData.processingId}
          onClose={issueActions.handleCloseStatusDialog}
          onStatusChange={issueActions.handleStatusChange}
          onNewStatusChange={issueActions.setNewStatus}
          onDescriptionChange={issueActions.setStatusDescription}
        />
      )}

      {canAssignIssue && (
        <AssignDialog
          open={issueActions.dialogs.assign}
          selectedIssue={selectedIssue}
          selectedUser={issueActions.assignData.selectedUser}
          usersList={issuesData.usersList}
          processingId={issuesData.processingId}
          onClose={issueActions.handleCloseAssignDialog}
          onUserChange={issueActions.setSelectedUser}
          onAssign={issueActions.handleAssign}
        />
      )}

      {canViewAssignedUsers && (
        <AssignedUsersDialog
          open={issueActions.dialogs.assignedUsers}
          assignedUsers={issueActions.assignedUsers}
          assignedUsersLoading={issueActions.assignedUsersLoading}
          onClose={issueActions.handleCloseAssignedUsersDialog}
        />
      )}
    </PageLayout>
  );
};

export default IssueList;